"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.canonicalTimezone = canonicalTimezone;
exports.isTimezone = isTimezone;
exports.effectiveTimezone = effectiveTimezone;
exports.formatDateTime = formatDateTime;
exports.formatUtcOffset = formatUtcOffset;
exports.timezoneRegion = timezoneRegion;
exports.timezoneCity = timezoneCity;
/** Longest real IANA id is under 40 characters; anything longer is not worth asking Intl about. */
const MAX_TIMEZONE_LENGTH = 64;
const STYLES = {
    short: { year: 'numeric', month: 'long', day: 'numeric' },
    /** Operation cards: the date plus the time to the minute; seconds never helped anyone tell two operations apart. */
    long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    },
    /** Timeline rows: as compact as still unambiguous within a year. */
    stamp: { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
};
/**
 * CLDR keeps a zone id stable once assigned and lags IANA tzdata when a link's primary name is
 * renamed (2022b: Kiev → Kyiv), so `Intl.supportedValuesOf('timeZone')` and even a device's
 * `resolvedOptions().timeZone` can still return the old spelling years later, while the new
 * id resolves fine everywhere. Map the holdovers we care about to the current IANA name.
 */
const CANONICAL_TIMEZONES = {
    'Europe/Kiev': 'Europe/Kyiv',
    'Asia/Calcutta': 'Asia/Kolkata',
};
/** The current IANA name for a zone id; unchanged when there is no known rename. */
function canonicalTimezone(zone) {
    return CANONICAL_TIMEZONES[zone] ?? zone;
}
/** True when `value` is an IANA zone identifier this runtime's ICU accepts. */
function isTimezone(value) {
    if (typeof value !== 'string' || value.length === 0 || value.length > MAX_TIMEZONE_LENGTH)
        return false;
    try {
        new Intl.DateTimeFormat('en', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
/** The manual choice wins; otherwise the device zone; UTC until the device has reported one. */
function effectiveTimezone(pref) {
    return pref.timezone ?? pref.deviceTimezone ?? 'UTC';
}
/** A stored zone this runtime does not know (tzdata renamed it) must not blank a screen. */
function safeZone(timezone) {
    return isTimezone(timezone) ? timezone : 'UTC';
}
/**
 * `iso` must be a valid ISO-8601 timestamp (callers get it from a zod `z.iso.datetime()` field
 * or from a `Date`); an unparseable string makes `Intl` throw `RangeError`, which is a caller
 * bug this function does not hide.
 */
function formatDateTime(iso, locale, timezone, style) {
    return new Intl.DateTimeFormat(locale, { ...STYLES[style], timeZone: safeZone(timezone) }).format(new Date(iso));
}
/**
 * "UTC+03:00", "UTC-05:00" or "UTC": the zone's offset in force at `at`.
 * `at` must be a valid Date; an invalid Date makes `Intl` throw `RangeError`.
 */
function formatUtcOffset(timezone, at) {
    const parts = new Intl.DateTimeFormat('en', {
        timeZone: safeZone(timezone),
        timeZoneName: 'longOffset',
    }).formatToParts(at);
    const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'GMT';
    const offset = name.replace(/^GMT/, 'UTC');
    return offset === 'UTC' || offset === 'UTC+00:00' ? 'UTC' : offset;
}
/** "Europe" from "Europe/Kyiv"; "Other" for ids without a slash such as "UTC". */
function timezoneRegion(zone) {
    const slash = zone.indexOf('/');
    return slash === -1 ? 'Other' : zone.slice(0, slash);
}
/** "Buenos Aires" from "America/Argentina/Buenos_Aires"; the id itself when there is no slash. */
function timezoneCity(zone) {
    const slash = zone.lastIndexOf('/');
    return (slash === -1 ? zone : zone.slice(slash + 1)).replace(/_/g, ' ');
}
//# sourceMappingURL=timezone.js.map